import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { TableSort } from "@src/shared/constants/sort-order.constants";
import type { RootState } from "@src/redux/store";
import {
  appUpdateCategoryService,
  type AppUpdateCategoryListItem,
  type AppUpdateCategoryOption,
  type GetAppUpdateCategoriesParams,
  type CreateAppUpdateCategoryPayload,
  type UpdateAppUpdateCategoryPayload,
} from "@src/@api-services/appUpdate";

interface AppUpdateCategoryState {
  data: AppUpdateCategoryListItem[];
  allCategories: AppUpdateCategoryOption[];
  total: number;
  params: GetAppUpdateCategoriesParams;
  loading: boolean;
  actionLoading: boolean;
  error: string | null;
}

export const getAppUpdateCategories = createAsyncThunk(
  "appUpdateCategories/getAppUpdateCategories",
  async (params: GetAppUpdateCategoriesParams, { rejectWithValue }) => {
    try {
      const response = await appUpdateCategoryService.getCategories(params);
      const rawData = response.data?.data?.data ?? [];
      const totalRecord = response.data?.data?.totalRecord ?? 0;
      return {
        params,
        data: rawData as AppUpdateCategoryListItem[],
        total: Number(totalRecord),
      };
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      return rejectWithValue(
        err.response?.data?.message || err.message || "Failed to fetch app update categories",
      );
    }
  },
);

export const getAllAppUpdateCategories = createAsyncThunk(
  "appUpdateCategories/getAllAppUpdateCategories",
  async (_, { rejectWithValue }) => {
    try {
      const response = await appUpdateCategoryService.getAllCategories();
      return (response.data?.data ?? []) as AppUpdateCategoryOption[];
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      return rejectWithValue(
        err.response?.data?.message || err.message || "Failed to fetch category options",
      );
    }
  },
);

export const createAppUpdateCategory = createAsyncThunk(
  "appUpdateCategories/createAppUpdateCategory",
  async (
    payload: CreateAppUpdateCategoryPayload,
    { dispatch, getState, rejectWithValue },
  ) => {
    try {
      const response = await appUpdateCategoryService.createCategory(payload);
      const state = getState() as RootState;
      if (state.appUpdateCategories?.params) {
        await dispatch(getAppUpdateCategories(state.appUpdateCategories.params));
      }
      return response.data;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      return rejectWithValue(
        err.response?.data?.message || err.message || "Failed to create category",
      );
    }
  },
);

export const updateAppUpdateCategory = createAsyncThunk(
  "appUpdateCategories/updateAppUpdateCategory",
  async (
    {
      id,
      categoryData,
    }: {
      id: number;
      categoryData: UpdateAppUpdateCategoryPayload;
    },
    { dispatch, getState, rejectWithValue },
  ) => {
    try {
      const response = await appUpdateCategoryService.updateCategory(id, categoryData);
      const state = getState() as RootState;
      if (state.appUpdateCategories?.params) {
        await dispatch(getAppUpdateCategories(state.appUpdateCategories.params));
      }
      return response.data;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      return rejectWithValue(
        err.response?.data?.message || err.message || "Failed to update category",
      );
    }
  },
);

export const deleteAppUpdateCategory = createAsyncThunk(
  "appUpdateCategories/deleteAppUpdateCategory",
  async (id: string | number, { dispatch, getState, rejectWithValue }) => {
    try {
      const response = await appUpdateCategoryService.deleteCategory(id);
      const state = getState() as RootState;
      if (state.appUpdateCategories?.params) {
        await dispatch(getAppUpdateCategories(state.appUpdateCategories.params));
      }
      return response.data;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      return rejectWithValue(
        err.response?.data?.message || err.message || "Failed to delete category",
      );
    }
  },
);

const initialState: AppUpdateCategoryState = {
  data: [],
  allCategories: [],
  total: 0,
  params: {
    page: 1,
    pageSize: 10,
    orderBy: "createdAt",
    orderDirection: TableSort.DESC,
  },
  loading: false,
  actionLoading: false,
  error: null,
};

export const appUpdateCategorySlice = createSlice({
  name: "appUpdateCategories",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAppUpdateCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAppUpdateCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
        state.total = action.payload.total;
        state.params = action.payload.params;
      })
      .addCase(getAppUpdateCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Failed to load categories";
      })
      .addCase(getAllAppUpdateCategories.fulfilled, (state, action) => {
        state.allCategories = action.payload;
      })
      .addCase(createAppUpdateCategory.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(createAppUpdateCategory.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(createAppUpdateCategory.rejected, (state) => {
        state.actionLoading = false;
      })
      .addCase(updateAppUpdateCategory.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(updateAppUpdateCategory.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(updateAppUpdateCategory.rejected, (state) => {
        state.actionLoading = false;
      })
      .addCase(deleteAppUpdateCategory.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(deleteAppUpdateCategory.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(deleteAppUpdateCategory.rejected, (state) => {
        state.actionLoading = false;
      });
  },
});

export default appUpdateCategorySlice.reducer;
